const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);

function pageDifference(previousPage = {}, nextPage = {}, pageIndex) {
  const supportedKeys = [
    "//",
    "valid",
    "page_index",
    "lightness",
    "speed_ms",
    "color",
    "word_page",
    "frames",
    "keyframes",
    "spotlight_frames",
  ];
  const extra = (page) =>
    Object.keys(page).filter((key) => !supportedKeys.includes(key));
  const unsupported = [
    ...new Set([...extra(previousPage), ...extra(nextPage)]),
  ].filter((key) => !same(previousPage[key], nextPage[key]));

  return {
    pageIndex,
    pageControl: !same(
      [
        previousPage.valid,
        previousPage.page_index,
        previousPage.lightness,
        previousPage.speed_ms,
        previousPage.color,
      ],
      [
        nextPage.valid,
        nextPage.page_index,
        nextPage.lightness,
        nextPage.speed_ms,
        nextPage.color,
      ]
    ),
    pageText: !same(previousPage.word_page, nextPage.word_page),
    pageRgb: !same(previousPage.frames, nextPage.frames),
    axisRgb: !same(previousPage.keyframes, nextPage.keyframes),
    carLight: !same(previousPage.spotlight_frames, nextPage.spotlight_frames),
    unsupported,
  };
}

export function diffR6Profile(previousData = {}, nextData = {}) {
  const previousLayers = previousData.key_layer?.layer_data || [];
  const nextLayers = nextData.key_layer?.layer_data || [];
  const layerStructureChanged =
    previousData.key_layer?.layer_num !== nextData.key_layer?.layer_num ||
    previousLayers.length !== nextLayers.length ||
    previousLayers.some(
      (item, index) =>
        (item?.layer || []).length !== (nextLayers[index]?.layer || []).length
    );
  const keyChanges = [];

  if (!layerStructureChanged) {
    previousLayers.forEach((previousLayer, layer_num) => {
      const before = previousLayer.layer || [];
      const after = nextLayers[layer_num].layer || [];
      before.forEach((old_key, keyIndex) => {
        const new_key = after[keyIndex];
        if (old_key === new_key) return;
        keyChanges.push({
          layer_num,
          row_num: Math.floor(keyIndex / 25),
          index: keyIndex % 25,
          old_key,
          new_key,
        });
      });
    });
  }

  const previousPages = previousData.page_data || [];
  const nextPages = nextData.page_data || [];
  const previousPageByIndex = new Map(
    previousPages.map((page, index) => [page.page_index ?? index, page])
  );
  const pageChanges = nextPages
    .map((page, index) =>
      pageDifference(
        previousPageByIndex.get(page.page_index ?? index),
        page,
        page.page_index ?? index
      )
    )
    .filter((change) =>
      [
        change.pageControl,
        change.pageText,
        change.pageRgb,
        change.axisRgb,
        change.carLight,
        change.unsupported.length > 0,
      ].some(Boolean)
    );

  const knownSections = new Set([
    "product_info",
    "page_num",
    "page_data",
    "key_layer",
    "tab_key",
    "tab_key_num",
    "macro_key",
    "MACRO_key",
    "MACRO_key_num",
    "exchange_num",
    "exchange_key",
    "Fn_key_num",
    "Fn_key",
    "swap_key_num",
    "swap_key",
  ]);
  const unsupportedSections = Object.keys({
    ...previousData,
    ...nextData,
  }).filter(
    (key) => !knownSections.has(key) && !same(previousData[key], nextData[key])
  );
  if (!same(previousData.product_info, nextData.product_info)) {
    unsupportedSections.push("product_info");
  }
  if (previousData.page_num !== nextData.page_num) {
    unsupportedSections.push("page_num");
  }
  if (previousPages.length !== nextPages.length) {
    unsupportedSections.push("page_data length");
  }

  return {
    layerStructureChanged,
    keyLayerChanged: !same(previousData.key_layer, nextData.key_layer),
    keyChanges,
    macroChanged:
      !same(previousData.macro_key, nextData.macro_key) ||
      !same(previousData.MACRO_key, nextData.MACRO_key),
    pressHoldChanged: !same(previousData.tab_key, nextData.tab_key),
    keyFunctionsChanged: [
      "exchange_num",
      "exchange_key",
      "Fn_key_num",
      "Fn_key",
      "swap_key_num",
      "swap_key",
    ].some((key) => !same(previousData[key], nextData[key])),
    pageChanges,
    unsupportedSections: [
      ...unsupportedSections,
      ...pageChanges.flatMap((change) => change.unsupported),
    ],
  };
}
